import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import Layout from '../../components/Layout';
import LicenseListFilters from '../../components/admin/licenses/LicenseListFilters';
import LicenseAdvancedFilters from '../../components/admin/licenses/LicenseAdvancedFilters';
import LicenseSavedViews from '../../components/admin/licenses/LicenseSavedViews';
import LicenseListTable from '../../components/admin/licenses/LicenseListTable';
import LicenseBulkActionsBar from '../../components/admin/licenses/LicenseBulkActionsBar';
import LicenseColumnToggleMenu from '../../components/admin/licenses/LicenseColumnToggleMenu';
import LicenseEditDrawer from '../../components/admin/licenses/LicenseEditDrawer';
import LicenseRevokeDialog from '../../components/admin/licenses/dialogs/LicenseRevokeDialog';
import LicenseSuspendDialog from '../../components/admin/licenses/dialogs/LicenseSuspendDialog';
import LicenseReactivateDialog from '../../components/admin/licenses/dialogs/LicenseReactivateDialog';
import LicenseExtendDialog from '../../components/admin/licenses/dialogs/LicenseExtendDialog';
import { useLicenseListQuery } from '../../hooks/useLicenseListQuery';
import { licensesManageService } from '../../api/licensesManageService';
import { useAbpPermissions } from '../../contexts/AbpPermissionsContext';
import {
  DEFAULT_LICENSE_FILTERS,
  LicenseStatus,
  LicenseType,
  type LicenseFilterState,
  type ManageLicenseListDto,
  type ManageLicensesListInput,
} from '../../types/manageLicenses';

// Inline helper — converts LicenseFilterState to API input (mirrors useLicenseListQuery's toInput).
function toInput(f: LicenseFilterState): ManageLicensesListInput {
  return {
    filterText: f.q || undefined,
    status: f.status === 'all' ? undefined : (f.status as LicenseStatus),
    licenseType: f.type === 'all' ? undefined : (f.type as LicenseType),
    publisherId: f.publisherIds[0],
    bookId: f.bookIds[0],
    booksellerId: f.booksellerIds[0],
    activatedAfter: f.activatedAfter,
    activatedBefore: f.activatedBefore,
    expiresAfter: f.expiresAfter,
    expiresBefore: f.expiresBefore,
    expiringWithinDays: f.expiringWithinDays,
    isPerpetual: f.isPerpetual,
    hasExternalLicenseId: f.hasExternalLicenseId,
    hasActiveSessions: f.hasActiveSessions,
    atOrOverDeviceLimit: f.atOrOverDeviceLimit || undefined,
    includeDeleted: f.includeDeleted,
    sorting: `${f.sort} ${f.dir}`,
    skipCount: (f.page - 1) * f.pageSize,
    maxResultCount: f.pageSize,
  };
}

export default function ManageLicensesPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  // ── URL → filter state ──────────────────────────────────────────────────────
  const filters = useMemo<LicenseFilterState>(() => {
    const f: LicenseFilterState = { ...DEFAULT_LICENSE_FILTERS };

    const q = params.get('q'); if (q) f.q = q;

    // status: accept numeric strings '1'–'4' and 'all'
    const status = params.get('status');
    if (status === 'all') f.status = 'all';
    else if (status === '1') f.status = LicenseStatus.Active;
    else if (status === '2') f.status = LicenseStatus.Expired;
    else if (status === '3') f.status = LicenseStatus.Revoked;
    else if (status === '4') f.status = LicenseStatus.Suspended;

    // type
    const type = params.get('type');
    if (type === 'all') f.type = 'all';
    else if (type === '1') f.type = LicenseType.Purchase;
    else if (type === '2') f.type = LicenseType.Rental;
    else if (type === '3') f.type = LicenseType.Subscription;

    // expiringWithinDays
    const ewd = Number(params.get('expiringWithinDays'));
    if (ewd === 7 || ewd === 30 || ewd === 90) f.expiringWithinDays = ewd;

    // single-value FK filters stored as arrays (0 or 1 element)
    const publisherId = params.get('publisherId'); if (publisherId) f.publisherIds = [publisherId];
    const bookId = params.get('bookId');           if (bookId)       f.bookIds = [bookId];
    const booksellerId = params.get('booksellerId'); if (booksellerId) f.booksellerIds = [booksellerId];

    const aa = params.get('activatedAfter');  if (aa) f.activatedAfter  = aa;
    const ab = params.get('activatedBefore'); if (ab) f.activatedBefore = ab;
    const ea = params.get('expiresAfter');    if (ea) f.expiresAfter    = ea;
    const eb = params.get('expiresBefore');   if (eb) f.expiresBefore   = eb;

    // tri-state booleans
    const isPerpetual = params.get('isPerpetual');
    if (isPerpetual === 'true') f.isPerpetual = true;
    else if (isPerpetual === 'false') f.isPerpetual = false;

    const hasExternal = params.get('hasExternalLicenseId');
    if (hasExternal === 'true') f.hasExternalLicenseId = true;
    else if (hasExternal === 'false') f.hasExternalLicenseId = false;

    const hasSessions = params.get('hasActiveSessions');
    if (hasSessions === 'true') f.hasActiveSessions = true;
    else if (hasSessions === 'false') f.hasActiveSessions = false;

    // plain booleans (absent → false)
    if (params.get('atOrOverDeviceLimit') === 'true') f.atOrOverDeviceLimit = true;
    if (params.get('includeDeleted') === 'true') f.includeDeleted = true;

    // sort / dir / pagination
    const sort = params.get('sort') as LicenseFilterState['sort'] | null;
    const validSorts: LicenseFilterState['sort'][] = [
      'expirationDate', 'activationDate', 'status', 'lastModificationTime',
      'activeDeviceCount', 'studentEmail', 'bookTitle',
    ];
    if (sort && validSorts.includes(sort)) f.sort = sort;

    const dir = params.get('dir');
    if (dir === 'asc' || dir === 'desc') f.dir = dir;

    const page = Number(params.get('page'));       if (page > 0) f.page = page;
    const pageSize = Number(params.get('pageSize')); if (pageSize > 0) f.pageSize = pageSize;

    return f;
  }, [params]);

  // ── filter state → URL ──────────────────────────────────────────────────────
  const writeFiltersToUrl = (next: LicenseFilterState) => {
    const out = new URLSearchParams();
    const set = (k: string, v: unknown) => {
      if (v === undefined || v === null || v === '' || v === false) return;
      out.set(k, String(v));
    };

    set('q', next.q);
    // Emit status/type only when not 'all'
    if (next.status !== 'all') set('status', String(next.status));
    if (next.type !== 'all')   set('type', String(next.type));
    set('expiringWithinDays', next.expiringWithinDays);
    // Arrays: emit first element only
    if (next.publisherIds.length > 0) set('publisherId', next.publisherIds[0]);
    if (next.bookIds.length > 0)      set('bookId', next.bookIds[0]);
    if (next.booksellerIds.length > 0) set('booksellerId', next.booksellerIds[0]);
    set('activatedAfter', next.activatedAfter);
    set('activatedBefore', next.activatedBefore);
    set('expiresAfter', next.expiresAfter);
    set('expiresBefore', next.expiresBefore);
    // Tri-state booleans: emit only when defined
    if (next.isPerpetual !== undefined)          set('isPerpetual', String(next.isPerpetual));
    if (next.hasExternalLicenseId !== undefined) set('hasExternalLicenseId', String(next.hasExternalLicenseId));
    if (next.hasActiveSessions !== undefined)    set('hasActiveSessions', String(next.hasActiveSessions));
    // Plain booleans: emit only when true
    set('atOrOverDeviceLimit', next.atOrOverDeviceLimit);
    set('includeDeleted', next.includeDeleted);
    set('sort', next.sort);
    set('dir', next.dir);
    set('page', next.page);
    set('pageSize', next.pageSize);

    setParams(out);
  };

  // ── data ────────────────────────────────────────────────────────────────────
  const { data, isLoading } = useLicenseListQuery(filters);
  const rows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  // ── selection ───────────────────────────────────────────────────────────────
  const [selection, setSelection] = useState<Map<string, ManageLicenseListDto>>(new Map());

  const toggleRow = (row: ManageLicenseListDto) => {
    setSelection(prev => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id); else next.set(row.id, row);
      return next;
    });
  };

  const togglePage = (pageRows: ManageLicenseListDto[], all: boolean) => {
    setSelection(prev => {
      const next = new Map(prev);
      if (all) pageRows.forEach(r => next.set(r.id, r));
      else     pageRows.forEach(r => next.delete(r.id));
      return next;
    });
  };

  const clearSelection = () => setSelection(new Map());

  // Reset selection whenever filters change
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // ── drawer / dialog ─────────────────────────────────────────────────────────
  const [activeLicense, setActiveLicense] = useState<ManageLicenseListDto | null>(null);
  const [dialog, setDialog] = useState<null | 'revoke' | 'suspend' | 'reactivate' | 'extend'>(null);

  // ── select-all matching filter ───────────────────────────────────────────────
  const selectAllMatchingFilter = async () => {
    const ids = await licensesManageService.idsForFilter(toInput(filters), 500);
    setSelection(prev => {
      const next = new Map(prev);
      ids.forEach(id => {
        if (!next.has(id)) {
          next.set(id, {
            id,
            studentId: '', studentEmail: '', studentName: '',
            bookId: '', bookTitle: '',
            licenseType: LicenseType.Purchase,
            status: LicenseStatus.Active,
            maxConcurrentDevices: 0, activeDeviceCount: 0,
            creationTime: new Date().toISOString(),
            isDeleted: false,
          } as ManageLicenseListDto);
        }
      });
      return next;
    });
  };

  // ── export ──────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const blob = await licensesManageService.exportExcel(toInput(filters));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'licenses-export.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ── permissions ─────────────────────────────────────────────────────────────
  const { hasPermission } = useAbpPermissions();
  const canRevoke  = hasPermission('learnleaf.App.RevokeLicense');
  const canSuspend = hasPermission('learnleaf.App.SuspendLicense');
  const canManage  = hasPermission('learnleaf.App.ManageLicenses');

  const isPageFullySelected = rows.length > 0 && rows.every(r => selection.has(r.id));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-2xl font-semibold mb-4">{t('App.Admin.Licenses.ManageLicensesPage.Title')}</h1>

        <LicenseSavedViews
          filters={filters}
          onApply={(f) => writeFiltersToUrl(f)}
        />

        <LicenseListFilters
          filters={filters}
          onChange={(patch) => writeFiltersToUrl({ ...filters, ...patch })}
        />

        <LicenseAdvancedFilters
          filters={filters}
          onChange={(patch) => writeFiltersToUrl({ ...filters, ...patch })}
        />

        <div className="flex items-center justify-end mb-2">
          <LicenseColumnToggleMenu />
        </div>

        <LicenseBulkActionsBar
          selectionCount={selection.size}
          totalCount={totalCount}
          isPageFullySelected={isPageFullySelected}
          filters={filters}
          onOpenDialog={setDialog}
          onExport={handleExport}
          onSelectAllMatchingFilter={selectAllMatchingFilter}
          onClearSelection={clearSelection}
          permissions={{ canRevoke, canSuspend, canManage }}
        />

        <LicenseListTable
          rows={rows}
          totalCount={totalCount}
          isLoading={isLoading}
          filters={filters}
          onChange={(patch) => writeFiltersToUrl({ ...filters, ...patch })}
          selection={selection}
          onToggleRow={toggleRow}
          onTogglePage={togglePage}
          onOpen={(id) => setActiveLicense(rows.find(r => r.id === id) ?? null)}
          onRequestRowAction={(row, action) => {
            setSelection(new Map([[row.id, row]]));
            setDialog(action);
          }}
        />
      </div>

      <LicenseEditDrawer
        license={activeLicense}
        onClose={() => setActiveLicense(null)}
      />

      <LicenseRevokeDialog
        open={dialog === 'revoke'}
        licenses={[...selection.values()]}
        onClose={() => setDialog(null)}
        onCompleted={() => { clearSelection(); }}
      />
      <LicenseSuspendDialog
        open={dialog === 'suspend'}
        licenses={[...selection.values()]}
        onClose={() => setDialog(null)}
        onCompleted={() => { clearSelection(); }}
      />
      <LicenseReactivateDialog
        open={dialog === 'reactivate'}
        licenses={[...selection.values()]}
        onClose={() => setDialog(null)}
        onCompleted={() => { clearSelection(); }}
      />
      <LicenseExtendDialog
        open={dialog === 'extend'}
        licenses={[...selection.values()]}
        onClose={() => setDialog(null)}
        onCompleted={() => { clearSelection(); }}
      />
    </Layout>
  );
}
